'use client';

import { Alert, Box, CircularProgress, Divider, Paper, Stack, Typography } from '@mui/material';
import { AppLink } from '@/components/mui/app-link';
import { AppLinkButton } from '@/components/mui/app-link-button';
import type { InstrumentRow } from '@/components/catalogo/types';
import { displayInstrument } from '@/libs/normalize-ticker';
import type { CatalogDefaultRanking } from '@/libs/catalogo-default-rankings';
import { encodeCatalogoVisibleColumnsBitset } from '@/libs/catalogo-compact-state';
import { useAppColors } from '@/styles/colors';

export type RankingPreviewState = {
  loading: boolean;
  error: string | null;
  rows: InstrumentRow[];
  total: number;
};

const PERCENT_FORMATTER = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const INTEGER_FORMATTER = new Intl.NumberFormat('pt-BR', {
  maximumFractionDigits: 0,
});
const CURRENCY_COMPACT_FORMATTER = new Intl.NumberFormat('pt-BR', {
  notation: 'compact',
  compactDisplay: 'short',
  maximumFractionDigits: 2,
});

const formatPercent = (value: number | null | undefined) => {
  if (value === null || value === undefined || Number.isNaN(value)) return '-';
  return `${PERCENT_FORMATTER.format(value)}%`;
};

const formatPvp = (value: number | null | undefined) => {
  if (value === null || value === undefined || Number.isNaN(value)) return '-';
  return value.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatPrice = (value: number | null | undefined) => {
  if (value === null || value === undefined || Number.isNaN(value)) return '-';
  return value.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatInteger = (value: number | null | undefined) => {
  if (value === null || value === undefined || Number.isNaN(value)) return '-';
  return INTEGER_FORMATTER.format(value);
};

const formatCurrencyCompact = (value: number | null | undefined) => {
  if (value === null || value === undefined || Number.isNaN(value)) return '-';
  return `R$ ${CURRENCY_COMPACT_FORMATTER.format(value)}`;
};

type MetricConfig = {
  label: string;
  value: (row: InstrumentRow) => number | null | undefined;
  format: (value: number | null | undefined) => string;
};

const METRIC_BY_FIELD: Record<string, MetricConfig> = {
  dy12mPct: { label: 'DY 12m', value: (row) => row.dy12mPct, format: formatPercent },
  liquidezDiaria30d: {
    label: 'Liq. 30d',
    value: (row) => row.liquidezDiaria30d,
    format: formatCurrencyCompact,
  },
  patrimonioLiquido: {
    label: 'Patrimônio',
    value: (row) => row.patrimonioLiquido,
    format: formatCurrencyCompact,
  },
  currentPvp: { label: 'P/VP', value: (row) => row.currentPvp, format: formatPvp },
  currentPrice: { label: 'Cotação', value: (row) => row.currentPrice, format: formatPrice },
  vpPerShareGrowth12mPct: {
    label: 'Cresc. VP/cota',
    value: (row) => row.vpPerShareGrowth12mPct,
    format: formatPercent,
  },
  distanceTo12mHighPct: {
    label: 'Dist. 12m',
    value: (row) => row.distanceTo12mHighPct,
    format: formatPercent,
  },
  vacanciaPct: { label: 'Vacância', value: (row) => row.vacanciaPct, format: formatPercent },
  inadimplenciaPct: {
    label: 'Inadimplência',
    value: (row) => row.inadimplenciaPct,
    format: formatPercent,
  },
  selicSensitivityScore: {
    label: 'Selic',
    value: (row) => row.selicSensitivityScore,
    format: formatPercent,
  },
  leverageIndexScore: {
    label: 'Alav.',
    value: (row) => row.leverageIndexScore,
    format: formatPercent,
  },
  concentracaoTijoloPct: {
    label: 'Conc. Tijolo',
    value: (row) => row.concentracaoTijoloPct,
    format: formatPercent,
  },
  concentracaoPapelPct: {
    label: 'Conc. Papel',
    value: (row) => row.concentracaoPapelPct,
    format: formatPercent,
  },
  numeroCotistas: {
    label: 'Cotistas',
    value: (row) => row.numeroCotistas,
    format: formatInteger,
  },
};

const RANKING_BASE_COLUMNS = ['idCem', 'name', 'dy12mPct', 'currentPvp'];
const RANKING_TARGET_COLUMN_COUNT = 7;
const RANKING_FALLBACK_COLUMNS = [
  'liquidezDiaria30d',
  'numeroCotistas',
  'patrimonioLiquido',
  'distanceTo12mHighPct',
  'vacanciaPct',
  'inadimplenciaPct',
  'selicSensitivityScore',
  'leverageIndexScore',
];
const SORT_FIELD_TO_COLUMN: Record<string, string> = {
  dy12mPct: 'dy12mPct',
  liquidezDiaria30d: 'liquidezDiaria30d',
  patrimonioLiquido: 'patrimonioLiquido',
  currentPvp: 'currentPvp',
  vpPerShareGrowth12mPct: 'vpPerShareGrowth12mPct',
  distanceTo12mHighPct: 'distanceTo12mHighPct',
  selicSensitivityScore: 'selicSensitivityScore',
  leverageIndexScore: 'leverageIndexScore',
  vacanciaPct: 'vacanciaPct',
  inadimplenciaPct: 'inadimplenciaPct',
  concentracaoTijoloPct: 'concentracaoTijoloPct',
  concentracaoPapelPct: 'concentracaoPapelPct',
};

const buildRankingVisibleColumns = (sort: string): string[] => {
  const out: string[] = [...RANKING_BASE_COLUMNS];
  const has = (field: string) => out.includes(field);
  const tryAdd = (field: string | null | undefined) => {
    if (!field || has(field)) return;
    out.push(field);
  };

  const sortFields = sort
    .split(',')
    .map((chunk) => (chunk.split(':')[0] ?? '').trim())
    .filter(Boolean);

  sortFields.forEach((field) => {
    tryAdd(SORT_FIELD_TO_COLUMN[field] ?? null);
  });

  RANKING_FALLBACK_COLUMNS.forEach((field) => {
    if (out.length >= RANKING_TARGET_COLUMN_COUNT) return;
    tryAdd(field);
  });

  return out.slice(0, RANKING_TARGET_COLUMN_COUNT);
};

const buildCatalogUrl = (catalogPath: string, sort: string, queryString?: string) => {
  const params = new URLSearchParams(queryString ?? '');
  params.set('fundTypes', 'FII');
  params.set('sort', sort);
  params.set('c', encodeCatalogoVisibleColumnsBitset(buildRankingVisibleColumns(sort)));
  const query = params.toString();
  return query ? `${catalogPath}?${query}` : catalogPath;
};

const PRIMARY_ACTION_BUTTON_SX = {
  textTransform: 'none',
  fontWeight: 800,
  px: 1.8,
  minHeight: 38,
  borderRadius: 2,
};

type RankingCardProps = {
  ranking: CatalogDefaultRanking;
  effectiveSort: string;
  state: RankingPreviewState;
  sortLabel: string;
};

export function RankingCard({ ranking, effectiveSort, state, sortLabel }: RankingCardProps) {
  const appColors = useAppColors();
  const catalogUrl = buildCatalogUrl(ranking.catalogPath, effectiveSort, ranking.queryString);
  const sortMetricFields = effectiveSort
    .split(',')
    .map((entry) => (entry.split(':')[0] ?? '').trim())
    .filter((field, index, list) => field && list.indexOf(field) === index)
    .filter((field) => Boolean(METRIC_BY_FIELD[field]));
  const displayMetricFields = [...sortMetricFields];
  const fallbackMetricFields = ['currentPvp', 'currentPrice'];
  fallbackMetricFields.forEach((field) => {
    if (displayMetricFields.length >= 3) return;
    if (displayMetricFields.includes(field)) return;
    displayMetricFields.push(field);
  });
  if (displayMetricFields.length === 0) {
    displayMetricFields.push('dy12mPct', 'currentPvp', 'currentPrice');
  }
  const displayMetrics = displayMetricFields
    .slice(0, 3)
    .map((field) => METRIC_BY_FIELD[field])
    .filter(Boolean);
  const xsVisibleMetricCount = Math.max(1, Math.min(displayMetrics.length, 2));
  const xsGridTemplate = ['70px', ...Array.from({ length: xsVisibleMetricCount }, () => 'minmax(56px, 1fr)')].join(' ');
  const smGridTemplate = ['72px', ...Array.from({ length: displayMetrics.length }, () => 'minmax(58px, 1fr)')].join(
    ' ',
  );

  return (
    <Paper
      variant='outlined'
      sx={{
        p: 1.5,
        borderRadius: 2,
        borderColor: 'hsl(var(--border))',
        backgroundColor: 'hsl(var(--card))',
        transition: 'border-color 160ms ease, box-shadow 160ms ease',
        '&:hover': {
          borderColor: appColors.actions.accentOutline.hoverBorder,
          boxShadow: '0 8px 24px hsl(var(--foreground) / 0.08)',
        },
      }}
    >
      <Stack spacing={1.1} sx={{ height: '100%' }}>
        <Stack spacing={0.4}>
          <Typography variant='subtitle1' fontWeight={800}>
            {ranking.name}
          </Typography>
          <Typography
            variant='caption'
            sx={{
              color: 'hsl(var(--muted-foreground))',
              display: 'block',
            }}
          >
            Ordenação: {sortLabel}
          </Typography>
        </Stack>

        <Divider />

        {state.loading ? (
          <Stack direction='row' spacing={0.8} alignItems='center'>
            <CircularProgress size={14} />
            <Typography variant='caption' color='text.secondary'>
              Carregando prévia do ranking...
            </Typography>
          </Stack>
        ) : state.error ? (
          <Alert severity='warning' sx={{ py: 0.4 }}>
            {state.error}
          </Alert>
        ) : state.rows.length === 0 ? (
          <Typography variant='caption' color='text.secondary'>
            Nenhum resultado na prévia deste ranking.
          </Typography>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gap: 0.25,
              p: 0.95,
              border: '1px solid hsl(var(--border))',
              borderRadius: 1.5,
              bgcolor: 'hsl(var(--background) / 0.55)',
            }}
          >
            <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{ mb: 0.2 }}>
              <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 700 }}>
                Top 5 prévia
              </Typography>
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ fontWeight: 600, whiteSpace: 'nowrap', ml: 1 }}
              >
                {sortLabel}
              </Typography>
            </Stack>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: xsGridTemplate,
                  sm: smGridTemplate,
                },
                alignItems: 'center',
                columnGap: 0.8,
                px: 0.6,
                py: 0.35,
                borderRadius: 1,
                bgcolor: 'hsl(var(--muted) / 0.45)',
              }}
            >
              <Typography variant='caption' sx={{ fontWeight: 700, color: 'text.secondary', textAlign: 'left' }}>
                Instrument
              </Typography>
              {displayMetrics.map((metric, index) => (
                <Typography
                  key={`${metric.label}-header`}
                  variant='caption'
                  sx={{
                    fontWeight: 700,
                    color: 'text.secondary',
                    textAlign: 'right',
                    display: index >= 2 ? { xs: 'none', sm: 'block' } : 'block',
                  }}
                >
                  {metric.label}
                </Typography>
              ))}
            </Box>
            {state.rows.map((row) => (
              <Box
                key={row.id}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: xsGridTemplate,
                    sm: smGridTemplate,
                  },
                  alignItems: 'center',
                  columnGap: 0.8,
                  px: 0.6,
                  py: 0.22,
                  borderRadius: 1,
                  '&:nth-of-type(even)': { bgcolor: 'hsl(var(--muted) / 0.2)' },
                }}
              >
                {row.idCem ? (
                  <AppLink
                    href={`/ativo/${encodeURIComponent(displayInstrument(row.idCem, row.fundTypeSymbol))}`}
                    underline='hover'
                    variant='caption'
                    sx={{
                      fontWeight: 800,
                      fontSize: '0.74rem',
                      color: appColors.actions.accentOutline.text,
                      textAlign: 'left',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {displayInstrument(row.idCem, row.fundTypeSymbol)}
                  </AppLink>
                ) : (
                  <Typography
                    variant='caption'
                    sx={{
                      fontWeight: 800,
                      fontSize: '0.74rem',
                      color: appColors.actions.accentOutline.text,
                      textAlign: 'left',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    -
                  </Typography>
                )}
                {displayMetrics.map((metric, index) => (
                  <Typography
                    key={`${row.id}-${metric.label}`}
                    variant='caption'
                    color='text.secondary'
                    sx={{
                      textAlign: 'right',
                      fontVariantNumeric: 'tabular-nums',
                      display: index >= 2 ? { xs: 'none', sm: 'block' } : 'block',
                    }}
                  >
                    {metric.format(metric.value(row))}
                  </Typography>
                ))}
              </Box>
            ))}
          </Box>
        )}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 'auto' }}>
          <AppLinkButton
            href={catalogUrl}
            variant='contained'
            color='warning'
            size='small'
            sx={{
              ...PRIMARY_ACTION_BUTTON_SX,
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            Ver ranking no catálogo
          </AppLinkButton>
        </Stack>
      </Stack>
    </Paper>
  );
}
